import type { LandmarkHandle } from "../lib/mapLandmarkTransform";
import type { MapImage, MapLabel, MapLandmark } from "../lib/mapDocument";
import { assertNever } from "../lib/assertNever";
import { just, nothing, type Maybe } from "../lib/maybe";
import {
  MAP_INK_COLORS,
  MAP_DEFAULT_FILL,
  type MapDocument,
  type MapPathWeight,
  type MapPathStyle,
  type MapLabelFont,
} from "../lib/mapDocument";
import { MAP_LANDMARK_ICONS } from "../lib/mapLandmarkIcons";
import {
  type TerrainBrushStyle,
  LANDMASS_BRUSH_SIZE_DEFAULT,
  LANDMASS_COAST_ROUGHNESS_DEFAULT,
  clampLandmassBrushSize,
  clampLandmassCoastRoughness,
} from "../lib/mapLandmass";
import type { ScreenPoint, WorldPoint } from "../lib/mapGeometry";

export type MapTool =
  | "crop"
  | "select"
  | "line"
  | "fill"
  | "landmark"
  | "image"
  | "storyPath"
  | "label"
  | "erase"
  | "landmass"
  | "terrain"
  | "water";

export type WaterMode = "lake" | "river";

/** Click-draft tree for Water → River (main channel + Y-fork tributaries). */
export type RiverDraft = {
  readonly branches: readonly (readonly WorldPoint[])[];
  readonly active: number;
};

export function emptyRiverDraft(): RiverDraft {
  return { branches: [], active: 0 };
}

export function riverDraftHasPoints(draft: RiverDraft): boolean {
  return draft.branches.some((branch) => branch.length > 0);
}

export type Selection =
  | { kind: "path"; layerId: string; pathId: string }
  | { kind: "node"; layerId: string; nodeId: string; pathId: string }
  | { kind: "fill"; layerId: string; id: string }
  | { kind: "landmark"; layerId: string; id: string }
  | { kind: "image"; layerId: string; id: string }
  | { kind: "storyMarker"; id: string }
  | { kind: "label"; id: string };

/** Transient interaction state. The host owns the saved document; DOM objects
 * and animation frames stay in useMapTools.ts. Absence here is always Maybe. */
export type MapToolsModel = {
  readonly tool: MapTool;
  readonly cropDraft: Maybe<{ start: WorldPoint; end: WorldPoint }>;
  readonly cropDrawing: boolean;
  readonly cropHandle: Maybe<LandmarkHandle>;
  readonly selection: Maybe<Selection>;
  readonly inkColor: string;
  readonly fillColor: string;
  readonly inkWeight: MapPathWeight;
  readonly inkStyle: MapPathStyle;
  readonly draftPoints: WorldPoint[];
  readonly cursorWorld: Maybe<WorldPoint>;
  readonly panning: boolean;
  readonly spaceHeld: boolean;
  readonly panStart: Maybe<{
    screen: ScreenPoint;
    viewport: MapDocument["viewport"];
  }>;
  readonly dragging: Maybe<
    | { kind: "node"; layerId: string; nodeId: string; pathId: string }
    | { kind: "landmark"; layerId: string; id: string }
    | { kind: "image"; layerId: string; id: string }
    | { kind: "storyMarker"; id: string }
    | {
        kind: "resize-landmark";
        layerId: string;
        id: string;
        start: MapLandmark;
        handle: LandmarkHandle;
        pointerStart: WorldPoint;
      }
    | {
        kind: "resize-image";
        layerId: string;
        id: string;
        start: MapImage;
        handle: LandmarkHandle;
        pointerStart: WorldPoint;
      }
    | { kind: "label"; id: string }
    | {
        kind: "resize-label";
        id: string;
        start: MapLabel;
        handle: LandmarkHandle;
        pointerStart: WorldPoint;
        dimensions: { width: number; height: number };
      }
  >;
  readonly undoStack: string[];
  readonly redoStack: string[];
  readonly landmarkStamp: Maybe<string>;
  /** Selected Assets/ path for the image placement tool. */
  readonly imageStamp: Maybe<string>;
  readonly stampSize: number;
  readonly labelSize: number;
  readonly labelFont: MapLabelFont;
  readonly labelRotation: number;
  readonly labelCurve: number;
  readonly labelSpacing: number;
  readonly hoverResize: boolean;
  readonly fillHoverLoop: Maybe<string[]>;
  readonly pendingLabelClick: Maybe<{
    id: string;
    screen: ScreenPoint;
  }>;
  readonly pendingSelectClick: Maybe<{
    hit:
      | { kind: "landmark"; layerId: string; id: string }
      | { kind: "image"; layerId: string; id: string }
      | { kind: "storyMarker"; id: string }
      | { kind: "path"; layerId: string; pathId: string }
      | { kind: "node"; layerId: string; nodeId: string; pathId: string };
    screen: ScreenPoint;
    wasSelected: boolean;
  }>;
  readonly labelDraft: string;
  readonly labelDialogOpen: boolean;
  readonly labelPlacement: Maybe<WorldPoint>;
  readonly editingLabel: Maybe<{ id: string }>;
  readonly shortcutsOpen: boolean;
  /** Soft brush diameter in world units (Land / Water / Erase on land). */
  readonly landmassBrushSize: number;
  /** Coast jaggedness percent (0 smooth … 100 fjorded). Land + Water. */
  readonly landmassCoastRoughness: number;
  readonly terrainBrushStyle: TerrainBrushStyle;
  readonly terrainOpacity: number;
  readonly terrainCoverage: number;
  /** True while a landmass stroke is captured. */
  readonly landmassSculpting: boolean;
  /** Lake brush vs river polyline draft. */
  readonly waterMode: WaterMode;
  /** Unfinished river tree (cleared on tool switch / Escape / finish). */
  readonly riverDraft: RiverDraft;
};

export function initialMapToolsModel(): MapToolsModel {
  return {
    tool: "select",
    cropDraft: nothing,
    cropDrawing: false,
    cropHandle: nothing,
    selection: nothing,
    inkColor: MAP_INK_COLORS[0].value,
    fillColor: MAP_DEFAULT_FILL,
    inkWeight: "medium",
    inkStyle: "solid",
    draftPoints: [],
    cursorWorld: nothing,
    panning: false,
    spaceHeld: false,
    panStart: nothing,
    dragging: nothing,
    undoStack: [],
    redoStack: [],
    landmarkStamp: just(MAP_LANDMARK_ICONS[0]!.id),
    imageStamp: nothing,
    stampSize: 1,
    labelSize: 1,
    labelFont: "cartographic",
    labelRotation: 0,
    labelCurve: 0,
    labelSpacing: 0,
    hoverResize: false,
    fillHoverLoop: nothing,
    pendingLabelClick: nothing,
    pendingSelectClick: nothing,
    labelDraft: "",
    labelDialogOpen: false,
    labelPlacement: nothing,
    editingLabel: nothing,
    shortcutsOpen: false,
    landmassBrushSize: LANDMASS_BRUSH_SIZE_DEFAULT,
    landmassCoastRoughness: LANDMASS_COAST_ROUGHNESS_DEFAULT,
    terrainBrushStyle: "jagged",
    terrainOpacity: 100,
    terrainCoverage: 50,
    landmassSculpting: false,
    waterMode: "lake",
    riverDraft: emptyRiverDraft(),
  };
}

/** Field-change messages carry already resolved pointer/input values.
 * Multi-field actions below (tool switching, dialogs, gestures, history) keep
 * their invariants together so the canvas adapter cannot partially reset them. */
export type MapToolsMsg =
  | {
      type: "CropResizeStarted";
      handle: LandmarkHandle;
      start: WorldPoint;
      end: WorldPoint;
    }
  | {
      type: "CropDraftChanged";
      value: MapToolsModel["cropDraft"];
      drawing: boolean;
    }
  | { type: "LandmarkFocused"; layerId: string; id: string }
  | { type: "StoryMarkerFocused"; id: string }
  | { type: "SelectionChanged"; value: MapToolsModel["selection"] }
  | { type: "FillColorChanged"; value: string }
  | { type: "InkColorChanged"; value: MapToolsModel["inkColor"] }
  | { type: "InkStyleChanged"; value: MapPathStyle }
  | { type: "InkWeightChanged"; value: MapToolsModel["inkWeight"] }
  | { type: "DraftPointsChanged"; value: MapToolsModel["draftPoints"] }
  | { type: "RiverDraftChanged"; value: MapToolsModel["riverDraft"] }
  | { type: "WaterModeChanged"; value: WaterMode }
  | { type: "CursorWorldChanged"; value: MapToolsModel["cursorWorld"] }
  | { type: "SpaceHeldChanged"; value: MapToolsModel["spaceHeld"] }
  | { type: "DraggingChanged"; value: MapToolsModel["dragging"] }
  | { type: "LandmarkStampChanged"; value: MapToolsModel["landmarkStamp"] }
  | { type: "ImageStampChanged"; value: MapToolsModel["imageStamp"] }
  | { type: "StampSizeChanged"; value: MapToolsModel["stampSize"] }
  | { type: "LabelFontChanged"; value: MapLabelFont }
  | { type: "LabelCurveChanged"; value: number }
  | { type: "LabelSpacingChanged"; value: number }
  | { type: "LabelRotationChanged"; value: number }
  | { type: "LabelSizeChanged"; value: MapToolsModel["labelSize"] }
  | { type: "HoverResizeChanged"; value: MapToolsModel["hoverResize"] }
  | { type: "FillHoverLoopChanged"; value: MapToolsModel["fillHoverLoop"] }
  | {
      type: "PendingLabelClickChanged";
      value: MapToolsModel["pendingLabelClick"];
    }
  | {
      type: "PendingSelectClickChanged";
      value: MapToolsModel["pendingSelectClick"];
    }
  | { type: "LabelDraftChanged"; value: MapToolsModel["labelDraft"] }
  | { type: "ShortcutsToggled" }
  | { type: "ShortcutsClosed" }
  | { type: "LabelPlacementStarted"; point: WorldPoint }
  | {
      type: "LabelEditStarted";
      id: string;
      text: string;
      size: number;
    }
  | { type: "LabelDialogClosed"; size: number }
  | { type: "ToolSelected"; tool: MapTool }
  | { type: "SelectToolAdopted" }
  | { type: "DraftCancelled" }
  | { type: "EscapePressed" }
  | { type: "PointerReleased" }
  | { type: "PointerLeft" }
  | {
      type: "PanStarted";
      screen: ScreenPoint;
      viewport: MapDocument["viewport"];
    }
  | { type: "UndoRecorded"; snapshot: string }
  | { type: "UndoRequested"; snapshot: string }
  | { type: "RedoRequested"; snapshot: string }
  | { type: "TerrainBrushStyleChanged"; value: TerrainBrushStyle }
  | { type: "TerrainOpacityChanged"; value: number }
  | { type: "TerrainCoverageChanged"; value: number }
  | { type: "LandmassBrushSizeChanged"; value: number }
  | { type: "LandmassCoastRoughnessChanged"; value: number }
  | { type: "LandmassSculptStarted" }
  | { type: "LandmassSculptEnded" };

export type MapToolsCmd =
  { kind: "redraw" } | { kind: "restoreDocument"; snapshot: string };

// Discarding an unfinished line / river must clear anchors and cursor preview.
const cancelledDraft = {
  draftPoints: [] as WorldPoint[],
  riverDraft: emptyRiverDraft(),
  cursorWorld: nothing,
  cropDraft: nothing,
  cropDrawing: false,
  cropHandle: nothing,
};

export function updateMapTools(
  model: MapToolsModel,
  msg: MapToolsMsg,
): readonly [MapToolsModel, readonly MapToolsCmd[]] {
  switch (msg.type) {
    case "CropResizeStarted":
      return [
        {
          ...model,
          cropDrawing: true,
          cropHandle: just(msg.handle),
          cropDraft: just({ start: msg.start, end: msg.end }),
        },
        [{ kind: "redraw" }],
      ];
    case "CropDraftChanged":
      return [
        {
          ...model,
          cropDraft: msg.value,
          cropDrawing: msg.drawing,
          cropHandle: msg.drawing ? model.cropHandle : nothing,
        },
        [{ kind: "redraw" }],
      ];
    case "StoryMarkerFocused":
      return [
        {
          ...model,
          tool: "storyPath",
          selection: just({ kind: "storyMarker", id: msg.id }),
        },
        [],
      ];
    case "LandmarkFocused":
      return [
        {
          ...model,
          tool: "select",
          selection: just({
            kind: "landmark",
            layerId: msg.layerId,
            id: msg.id,
          }),
        },
        [],
      ];
    case "SelectionChanged":
      return [{ ...model, selection: msg.value }, []];
    case "FillColorChanged":
      return [{ ...model, fillColor: msg.value }, []];
    case "InkColorChanged":
      return [{ ...model, inkColor: msg.value }, []];
    case "InkStyleChanged":
      return [{ ...model, inkStyle: msg.value }, []];
    case "InkWeightChanged":
      return [{ ...model, inkWeight: msg.value }, []];
    case "DraftPointsChanged":
      return [{ ...model, draftPoints: msg.value }, []];
    case "RiverDraftChanged":
      return [{ ...model, riverDraft: msg.value }, []];
    case "WaterModeChanged":
      return [
        {
          ...model,
          waterMode: msg.value,
          riverDraft: emptyRiverDraft(),
          cursorWorld: nothing,
        },
        [{ kind: "redraw" }],
      ];
    case "CursorWorldChanged":
      return [{ ...model, cursorWorld: msg.value }, []];
    case "SpaceHeldChanged":
      return [{ ...model, spaceHeld: msg.value }, []];
    case "DraggingChanged":
      return [{ ...model, dragging: msg.value }, []];
    case "LandmarkStampChanged":
      return [{ ...model, landmarkStamp: msg.value }, []];
    case "ImageStampChanged":
      return [{ ...model, imageStamp: msg.value }, []];
    case "StampSizeChanged":
      return [{ ...model, stampSize: msg.value }, []];
    case "LabelFontChanged":
      return [{ ...model, labelFont: msg.value }, []];
    case "LabelCurveChanged":
      return [
        { ...model, labelCurve: Math.max(-180, Math.min(180, msg.value)) },
        [],
      ];
    case "LabelSpacingChanged":
      return [
        { ...model, labelSpacing: Math.max(0, Math.min(24, msg.value)) },
        [],
      ];
    case "LabelRotationChanged":
      return [
        { ...model, labelRotation: Math.max(-180, Math.min(180, msg.value)) },
        [],
      ];
    case "LabelSizeChanged":
      return [{ ...model, labelSize: msg.value }, []];
    case "HoverResizeChanged":
      return [{ ...model, hoverResize: msg.value }, []];
    case "FillHoverLoopChanged":
      return [{ ...model, fillHoverLoop: msg.value }, []];
    case "PendingLabelClickChanged":
      return [{ ...model, pendingLabelClick: msg.value }, []];
    case "PendingSelectClickChanged":
      return [{ ...model, pendingSelectClick: msg.value }, []];
    case "LabelDraftChanged":
      return [{ ...model, labelDraft: msg.value }, []];
    case "ShortcutsClosed":
      return [{ ...model, shortcutsOpen: false }, []];
    case "ShortcutsToggled":
      return [{ ...model, shortcutsOpen: model.shortcutsOpen === false }, []];
    case "LabelPlacementStarted":
      return [
        {
          ...model,
          editingLabel: nothing,
          selection: nothing,
          labelPlacement: just(msg.point),
          labelDraft: "",
          labelDialogOpen: true,
        },
        [{ kind: "redraw" }],
      ];
    case "LabelEditStarted":
      return [
        {
          ...model,
          editingLabel: just({ id: msg.id }),
          labelPlacement: nothing,
          labelDraft: msg.text,
          labelSize: msg.size,
          selection: just({ kind: "label", id: msg.id }),
          labelDialogOpen: true,
        },
        [{ kind: "redraw" }],
      ];
    case "LabelDialogClosed":
      return [
        {
          ...model,
          labelDialogOpen: false,
          editingLabel: nothing,
          labelPlacement: nothing,
          labelDraft: "",
          labelSize: msg.size,
        },
        [{ kind: "redraw" }],
      ];
    case "EscapePressed": {
      if (model.cropDraft.tag === "just")
        return [
          {
            ...model,
            cropDraft: nothing,
            cropDrawing: false,
            cropHandle: nothing,
          },
          [{ kind: "redraw" }],
        ];
      // Dialogs handle their own Escape. Story-path selections clear before
      // leaving the tool; other selections and hover previews are idle.
      if (model.labelDialogOpen || model.shortcutsOpen) return [model, []];
      const actionActive =
        model.draftPoints.length > 0 ||
        riverDraftHasPoints(model.riverDraft) ||
        model.panning ||
        model.dragging.tag === "just" ||
        model.pendingSelectClick.tag === "just" ||
        model.pendingLabelClick.tag === "just" ||
        model.landmassSculpting;
      return [
        {
          ...model,
          ...cancelledDraft,
          tool:
            actionActive ||
            (model.tool === "storyPath" && model.selection.tag === "just") ||
            (model.tool === "landmark" && model.landmarkStamp.tag === "just") ||
            (model.tool === "image" && model.imageStamp.tag === "just")
              ? model.tool
              : "select",
          landmarkStamp:
            model.tool === "landmark" ? nothing : model.landmarkStamp,
          imageStamp: model.tool === "image" ? nothing : model.imageStamp,
          selection: nothing,
          dragging: nothing,
          panning: false,
          panStart: nothing,
          pendingSelectClick: nothing,
          pendingLabelClick: nothing,
          hoverResize: false,
          fillHoverLoop: nothing,
          landmassSculpting: false,
        },
        [{ kind: "redraw" }],
      ];
    }
    case "DraftCancelled":
      return [{ ...model, ...cancelledDraft }, []];
    case "ToolSelected":
      if (model.tool === msg.tool) return [model, []];
      return [
        {
          ...model,
          ...cancelledDraft,
          tool: msg.tool,
          selection: nothing,
          dragging: nothing,
          pendingLabelClick: nothing,
          pendingSelectClick: nothing,
          hoverResize: false,
          fillHoverLoop: nothing,
          landmassSculpting: false,
        },
        [{ kind: "redraw" }],
      ];
    case "TerrainBrushStyleChanged":
      return [{ ...model, terrainBrushStyle: msg.value }, [{ kind: "redraw" }]];
    case "TerrainCoverageChanged":
      return [
        {
          ...model,
          terrainCoverage: Number.isFinite(msg.value)
            ? Math.max(5, Math.min(100, msg.value))
            : model.terrainCoverage,
        },
        [{ kind: "redraw" }],
      ];
    case "TerrainOpacityChanged":
      return [
        {
          ...model,
          terrainOpacity: Number.isFinite(msg.value)
            ? Math.max(5, Math.min(100, msg.value))
            : model.terrainOpacity,
        },
        [{ kind: "redraw" }],
      ];
    case "LandmassBrushSizeChanged":
      return [
        {
          ...model,
          landmassBrushSize: clampLandmassBrushSize(msg.value),
        },
        [{ kind: "redraw" }],
      ];
    case "LandmassCoastRoughnessChanged":
      return [
        {
          ...model,
          landmassCoastRoughness: clampLandmassCoastRoughness(msg.value),
        },
        [{ kind: "redraw" }],
      ];
    case "LandmassSculptStarted":
      return [{ ...model, landmassSculpting: true }, []];
    case "LandmassSculptEnded":
      return [{ ...model, landmassSculpting: false }, []];
    case "SelectToolAdopted":
      if (model.tool === "select") return [model, []];
      return [
        {
          ...model,
          ...cancelledDraft,
          tool: "select",
          pendingLabelClick: nothing,
          pendingSelectClick: nothing,
        },
        [],
      ];
    case "PanStarted":
      return [
        {
          ...model,
          panning: true,
          panStart: just({
            screen: msg.screen,
            viewport: { ...msg.viewport },
          }),
        },
        [],
      ];
    case "PointerReleased":
      return [
        {
          ...model,
          panning: false,
          panStart: nothing,
          dragging: nothing,
          pendingSelectClick: nothing,
          pendingLabelClick: nothing,
          landmassSculpting: false,
        },
        [],
      ];
    case "PointerLeft":
      if (
        model.panning ||
        model.dragging.tag === "just" ||
        model.landmassSculpting
      ) {
        return [model, []];
      }
      return [
        {
          ...model,
          cursorWorld: nothing,
          hoverResize: false,
          fillHoverLoop: nothing,
        },
        [{ kind: "redraw" }],
      ];
    // Capture before editing; a new edit invalidates the redo branch.
    case "UndoRecorded":
      return [
        {
          ...model,
          undoStack: [...model.undoStack.slice(-49), msg.snapshot],
          redoStack: [],
        },
        [],
      ];
    case "UndoRequested":
    case "RedoRequested": {
      const undoing = msg.type === "UndoRequested";
      const source = undoing ? model.undoStack : model.redoStack;
      const snapshot = source.at(-1);
      if (typeof snapshot === "undefined") return [model, []];
      return [
        {
          ...model,
          ...cancelledDraft,
          selection: nothing,
          undoStack: undoing
            ? model.undoStack.slice(0, -1)
            : [...model.undoStack, msg.snapshot],
          redoStack: undoing
            ? [...model.redoStack, msg.snapshot]
            : model.redoStack.slice(0, -1),
        },
        [{ kind: "restoreDocument", snapshot }, { kind: "redraw" }],
      ];
    }
    default:
      return assertNever(msg);
  }
}
